<template>
  <div class="p-6 md:p-10">
    <router-link to="/admin/ledger" class="text-sm font-semibold text-gray-500 hover:text-gray-700">&larr; Back to Companies</router-link>

    <div v-if="loading" class="py-16 text-center text-gray-400">Loading...</div>
    <div v-else-if="!company" class="py-16 text-center text-gray-500">Company not found.</div>

    <div v-else>
      <div class="mt-4 flex flex-wrap items-start justify-between gap-6">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-amber-500 font-semibold">{{ company.etdId }}</p>
          <h1 class="mt-1 text-3xl font-bold text-gray-900">{{ company.name }}</h1>
        </div>
        <div class="rounded-2xl bg-amber-50 px-6 py-4 text-right">
          <p class="text-xs uppercase tracking-wide text-amber-600">Balance Owed to Al Etimad</p>
          <p class="mt-0.5 text-2xl font-bold text-amber-700">{{ formattedBalance }}</p>
        </div>
      </div>

      <div class="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div>
          <label class="text-xs uppercase tracking-wide text-gray-500">Currency</label>
          <select v-model="currency" class="mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-amber-500">
            <option v-for="c in currencyOptions" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <router-link
          :to="`/admin/ledger/print/${company.id}`"
          target="_blank"
          class="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
        >
          Print Ledger
        </router-link>
        <span v-if="saved" class="text-sm font-medium text-green-600">Saved.</span>
      </div>

      <!-- Desktop/tablet: full table -->
      <div class="mt-6 hidden overflow-x-auto rounded-2xl border border-gray-100 bg-white sm:block">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th class="px-3 py-2.5 font-semibold">Date</th>
              <th class="px-3 py-2.5 font-semibold">Type</th>
              <th class="px-3 py-2.5 font-semibold">Ref#</th>
              <th class="px-3 py-2.5 font-semibold text-right">Debit</th>
              <th class="px-3 py-2.5 font-semibold text-right">Credit</th>
              <th class="px-3 py-2.5 font-semibold">Description</th>
              <th class="px-3 py-2.5 font-semibold text-right">Balance</th>
              <th class="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(row, idx) in rows" :key="idx">
              <td class="px-3 py-2"><input v-model="row.date" type="date" class="w-36 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" /></td>
              <td class="px-3 py-2">
                <select :value="isOtherType(row.type) ? '__other__' : row.type" @change="handleTypeChange(row, $event.target.value)" class="w-44 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500">
                  <option v-for="opt in typeOptions" :key="opt" :value="opt">{{ opt }}</option>
                  <option value="__other__">Other</option>
                </select>
                <input v-if="isOtherType(row.type)" v-model="row.type" type="text" placeholder="Custom type" class="mt-1 w-44 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
              </td>
              <td class="px-3 py-2"><input v-model="row.refNo" type="text" class="w-28 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" /></td>
              <td class="px-3 py-2"><input v-model.number="row.debit" type="number" class="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-right text-sm focus:outline-none focus:border-amber-500" /></td>
              <td class="px-3 py-2"><input v-model.number="row.credit" type="number" class="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-right text-sm focus:outline-none focus:border-amber-500" /></td>
              <td class="px-3 py-2"><input v-model="row.description" type="text" class="w-56 rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" /></td>
              <td class="px-3 py-2 text-right font-semibold text-gray-800">{{ formatBalanceAt(idx) }}</td>
              <td class="px-3 py-2 text-right">
                <button type="button" @click="rows.splice(idx, 1)" class="text-red-500 hover:text-red-600">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </td>
            </tr>
            <tr v-if="rows.length === 0">
              <td colspan="8" class="px-3 py-8 text-center text-gray-400">No entries yet. Add a row below.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: a wide fixed-column table doesn't fit a phone screen, so
           each entry becomes its own stacked card instead of forcing
           horizontal scroll through a data-entry table one field at a
           time. -->
      <div class="mt-6 space-y-3 sm:hidden">
        <div v-if="rows.length === 0" class="rounded-2xl border border-dashed border-gray-200 py-10 text-center text-gray-400">
          No entries yet. Add a row below.
        </div>
        <div v-for="(row, idx) in rows" :key="idx" class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Entry {{ idx + 1 }}</span>
            <button type="button" @click="rows.splice(idx, 1)" class="text-red-500 hover:text-red-600">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Date</label>
              <input v-model="row.date" type="date" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Ref#</label>
              <input v-model="row.refNo" type="text" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div class="col-span-2">
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Type</label>
              <select :value="isOtherType(row.type) ? '__other__' : row.type" @change="handleTypeChange(row, $event.target.value)" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500">
                <option v-for="opt in typeOptions" :key="opt" :value="opt">{{ opt }}</option>
                <option value="__other__">Other</option>
              </select>
              <input v-if="isOtherType(row.type)" v-model="row.type" type="text" placeholder="Custom type" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Debit</label>
              <input v-model.number="row.debit" type="number" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-right text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Credit</label>
              <input v-model.number="row.credit" type="number" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-right text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div class="col-span-2">
              <label class="text-[11px] uppercase tracking-wide text-gray-400">Description</label>
              <input v-model="row.description" type="text" class="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div class="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
            <span class="text-gray-500">Running Balance</span>
            <span class="font-semibold text-gray-900">{{ formatBalanceAt(idx) }}</span>
          </div>
        </div>
      </div>

      <div class="mt-4 flex items-center gap-3">
        <button
          type="button"
          @click="rows.push({ date: '', type: typeOptions[0], refNo: '', debit: 0, credit: 0, description: '' })"
          class="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
        >
          + Add Row
        </button>
        <button
          type="button"
          @click="save"
          :disabled="saving"
          class="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Save Ledger' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { listItems, addItem, updateItem, getItem } from '../../services/dataStore'
import { formatAmount } from '../../services/currencyStore'

const TYPE_OPTIONS = ['Hotel Booking Confirmed', 'Customer Payment', 'Hotel Booking Cancelled', 'Refund']

export default {
  name: 'LedgerEdit',
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      company: null,
      ledgerId: null,
      currency: 'PKR',
      rows: [],
      typeOptions: TYPE_OPTIONS,
      currencyOptions: ['PKR', 'SAR', 'USD', 'GBP']
    }
  },
  computed: {
    balance() {
      return this.rows.reduce((bal, row) => bal + (Number(row.debit) || 0) - (Number(row.credit) || 0), 0)
    },
    formattedBalance() {
      return formatAmount(this.balance, this.currency)
    }
  },
  async mounted() {
    const companyId = this.$route.params.companyId
    this.company = await getItem('companies', companyId)
    if (this.company) {
      const ledgers = await listItems('ledgers', [])
      const existing = ledgers.find(l => l.companyId === companyId)
      if (existing) {
        this.ledgerId = existing.id
        this.currency = existing.currency || 'PKR'
        this.rows = (existing.rows || []).map(r => ({ ...r }))
      }
    }
    this.loading = false
  },
  methods: {
    isOtherType(value) {
      return !TYPE_OPTIONS.includes(value)
    },
    formatBalanceAt(idx) {
      let bal = 0
      for (let i = 0; i <= idx; i++) {
        bal += (Number(this.rows[i].debit) || 0) - (Number(this.rows[i].credit) || 0)
      }
      return formatAmount(bal, this.currency)
    },
    handleTypeChange(row, selected) {
      row.type = selected === '__other__' ? '' : selected
    },
    async save() {
      this.saving = true
      const payload = { companyId: this.company.id, currency: this.currency, rows: this.rows }
      try {
        if (this.ledgerId) {
          await updateItem('ledgers', this.ledgerId, payload)
        } else {
          const created = await addItem('ledgers', payload)
          this.ledgerId = created.id
        }
        this.saved = true
        setTimeout(() => { this.saved = false }, 2500)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
